import { useMemo, useReducer } from 'react';

type ListEditState<T> = {
  isChanged: boolean;
  values: T[];
};

enum ListCommand {
  SET,
  ADD,
  REMOVE,
  UPDATE,
}

type ListAction<T> =
  | { type: ListCommand.SET; values: T[]; isChanged?: boolean }
  | {
      type: ListCommand.ADD;
      createItem?: (data?: Partial<T>) => T;
      data: Partial<T>;
    }
  | { type: ListCommand.REMOVE; index: number }
  | { type: ListCommand.UPDATE; index: number; data: Partial<T> };

function useListEditState<T>(
  createItem: (data?: Partial<T>) => T,
  isMissingFields: (item: T) => boolean,
  initial: T[] = [],
) {
  const reducer = (
    state: ListEditState<T>,
    action: ListAction<T>,
  ): ListEditState<T> => {
    switch (action.type) {
      case ListCommand.SET:
        return { isChanged: action.isChanged ?? false, values: action.values };
      case ListCommand.ADD:
        return {
          isChanged: true,
          values: [
            ...state.values,
            action.createItem?.(action?.data) ?? createItem(action?.data),
          ],
        };
      case ListCommand.REMOVE:
        if (action.index >= 0 && action.index < state.values.length) {
          return {
            isChanged: true,
            values: state.values.filter((_, i) => i !== action.index),
          };
        }
        return state;
      case ListCommand.UPDATE:
        if (action.index >= 0 && action.index < state.values.length) {
          return {
            isChanged: true,
            values: state.values.map((item, i) =>
              i === action.index ? { ...item, ...action.data } : item,
            ),
          };
        }
        return state;
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    isChanged: false,
    values: Array.isArray(initial) ? initial : [],
  });

  return {
    add: (data?: Partial<T>) => dispatch({ data, type: ListCommand.ADD }),
    isChanged: state.isChanged,
    isMissingFields: useMemo(
      () => Array.isArray(state.values) && state.values.some(isMissingFields),
      [state.values, isMissingFields],
    ),
    remove: (index: number) => dispatch({ index, type: ListCommand.REMOVE }),
    reset: (values: T[], isChanged?: boolean) =>
      dispatch({ isChanged, type: ListCommand.SET, values }),
    update: (index: number, data: Partial<T>) =>
      dispatch({ data, index, type: ListCommand.UPDATE }),
    values: state.values,
  };
}

export default useListEditState;
